import { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import korzaLogo from '@/assets/korza_logo_black_blue.png';
import { AuthBackdrop } from '@/pages/auth/auth-backdrop';
import {
  AUTH_EXIT_MS,
  AuthExitContext,
  authSlideDirection,
} from '@/pages/auth/auth-exit';

const EXIT_EASE = [0.7, 0, 0.2, 1] as const;

// Layout route of the auth entry points (`/login`, `/create-account`,
// `/login/success`): the two-column frame (logo + form column on the brand
// book's light ground, live 3D backdrop on the right) stays mounted across
// them, and only the form column animates the swap — so the backdrop's WebGL
// scene and its push demo keep running instead of restarting on every
// switch. Leaving for the dashboard (useLeaveAuth) slides the backdrop off.
export function AuthRouteTransition() {
  const location = useLocation();
  const navigate = useNavigate();
  const shouldReduceMotion = useReducedMotion();
  const queryClient = useQueryClient();
  const [leavingTo, setLeavingTo] = useState<string | null>(null);

  // Direction-aware: forward steps slide in from the right, going back from
  // the left — rather than a flat cross-fade, so the swap reads as motion
  // starting right at the click, not just a page blink.
  const direction = authSlideDirection(location.pathname);

  const leave = useCallback(
    (to: string) => setLeavingTo((current) => current ?? to),
    []
  );

  // The app-wide SelfProvider still holds the signed-out `self` (it only
  // fetched it on load): refetch it while the panel slides, or SelfGuard
  // would bounce the dashboard straight back to /login.
  useEffect(() => {
    if (leavingTo === null) return;
    let cancelled = false;
    void Promise.all([
      queryClient.refetchQueries({ queryKey: ['self'] }),
      new Promise((resolve) =>
        setTimeout(resolve, shouldReduceMotion ? 0 : AUTH_EXIT_MS)
      ),
    ]).then(() => {
      if (!cancelled) navigate(leavingTo, { replace: true });
    });
    return () => {
      cancelled = true;
    };
  }, [leavingTo, navigate, queryClient, shouldReduceMotion]);

  const leaving = leavingTo !== null;

  return (
    <AuthExitContext.Provider value={leave}>
      <div className="flex min-h-screen w-full flex-col overflow-x-clip bg-[#F9F9F9] text-[#15191E] lg:flex-row">
        <main className="flex flex-1 flex-col px-6 py-8 lg:px-12 lg:py-10">
          <img
            src={korzaLogo}
            alt="Korza"
            className="h-4 w-auto self-start lg:h-5"
          />
          <motion.div
            className="flex flex-1 items-center justify-center py-10 lg:py-12"
            initial={false}
            animate={leaving ? { opacity: 0, x: -24 } : { opacity: 1, x: 0 }}
            transition={{ duration: 0.35, ease: 'easeOut' }}
          >
            <div className="w-full max-w-[400px]">
              {shouldReduceMotion ? (
                <Outlet />
              ) : (
                <AnimatePresence mode="wait" initial={false}>
                  <motion.div
                    key={location.pathname}
                    initial={{ opacity: 0, x: 24 * direction }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -24 * direction }}
                    transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                  >
                    <Outlet />
                  </motion.div>
                </AnimatePresence>
              )}
            </div>
          </motion.div>
        </main>
        <motion.div
          data-leaving={leaving || undefined}
          className="relative hidden flex-1 overflow-hidden lg:sticky lg:top-0 lg:flex lg:h-screen"
          initial={false}
          animate={{ x: leaving ? '100%' : '0%' }}
          transition={{ duration: AUTH_EXIT_MS / 1000, ease: EXIT_EASE }}
        >
          <AuthBackdrop />
        </motion.div>
      </div>
    </AuthExitContext.Provider>
  );
}
